<template>
  <div class="customer-selection pa-4">
    <div class="d-flex justify-space-between align-center mb-6">
      <span class="text-h5">Customers Overview</span>
      <v-btn
        color="primary"
        class="add-btn"
        @click="newCustomerDialog = true"
      >
        <v-icon left class="mr-2">mdi-plus</v-icon>
        Add Customer
      </v-btn>
    </div>

    <div class="customers-container">
      <v-expansion-panels>
        <v-expansion-panel
          v-for="customer in customerList"
          :key="customer.id"
          class="mb-2 custom-panel"
        >
          <v-expansion-panel-title class="panel-title">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-icon color="primary" size="20" class="mr-2">
                  mdi-domain
                </v-icon>
                <v-text-field
                  v-if="editingCustomer === customer.id"
                  v-model="customer.name"
                  @blur="saveCustomerName(customer)"
                  @keyup.enter="saveCustomerName(customer)"
                  hide-details
                  dense
                  class="customer-edit-field mt-0 pt-0"
                  style="flex: 1 1 360px; width: 100%; min-width: 240px; max-width: 560px;"
                ></v-text-field>
                <span v-else @dblclick="startEditingCustomer(customer.id)">
                  {{ customer.name }}
                </span>
              </div>
              <div class="d-flex align-center">
                <v-btn
                  icon
                  density="comfortable"
                  variant="text"
                  color="primary"
                  @click.stop="viewDashboard(customer.id)"
                  title="View Dashboard"
                >
                  <v-icon size="18">mdi-view-dashboard</v-icon>
                </v-btn>
                <v-btn
                  icon
                  density="comfortable"
                  variant="text"
                  color="primary"
                  @click.stop="startEditingCustomer(customer.id)"
                >
                  <v-icon size="18">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  density="comfortable"
                  variant="text"
                  color="error"
                  @click.stop="confirmDeleteCustomer(customer.id, customer.name)"
                >
                  <v-icon size="18">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <department-project-manager
              :customer-id="customer.id"
              :departments="customer.departments || []"
              @department-created="loadCustomers"
              @department-updated="loadCustomers"
              @department-deleted="loadCustomers"
              @project-created="loadCustomers"
              @project-updated="loadCustomers"
              @project-deleted="loadCustomers"
              @workshop-created="loadCustomers"
              @workshop-deleted="loadCustomers"
              @workshop-updated="loadCustomers"
              @open-workshop="openWorkshop"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- New Customer Dialog -->
    <v-dialog v-model="newCustomerDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pa-4">
          Create a new Customer
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            data-automationId="newCustomerName"
            type="text"
            label="Name"
            v-model="newCustomerName"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end pa-4">
          <v-btn
            variant="text"
            color="grey-darken-1"
            @click="newCustomerDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            data-automationId="newCustomerCreateButton"
            color="primary"
            @click="createCustomer"
          >
            Create Customer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- New Department Dialog -->
    <v-dialog v-model="newDepartmentDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pa-4">
          Create a new Department
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newDepartmentName"
            label="Department Name"
            hide-details
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end pa-4">
          <v-btn
            variant="text"
            color="grey-darken-1"
            @click="newDepartmentDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="createDepartment"
          >
            Create Department
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Customer Dialog -->
    <v-dialog v-model="deleteCustomerDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6 pa-4">
          Delete Customer
        </v-card-title>
        <v-card-text class="pa-4">
          Are you sure you want to delete customer "{{ customerToDelete.name }}"? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="deleteCustomerDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            @click="deleteCustomerConfirmed"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  createWorkshop,
  createCustomer,
  deleteCustomer
} from "@/workshop/workshopFunctions";
import { updateCustomerName, createDepartment } from '@/workshop/departmentFunctions';
import DepartmentProjectManager from './DepartmentProjectManager.vue';
import { getDatabase, ref as dbRef, get } from 'firebase/database';

export default {
  name: "CustomerWorkshopSelection",
  components: {
    DepartmentProjectManager
  },
  data() {
    return {
      customerList: [],
      newCustomerDialog: false,
      newCustomerName: "",
      newDepartmentDialog: false,
      newDepartmentName: "",
      newWorkshopDialog: false,
      newWorkshopName: "",
      selectedCustomer: null,
      selectedDepartment: null,
      selectedProject: null,
      deleteCustomerDialog: false,
      customerToDelete: { id: null, name: "" },
      editingCustomer: null
    };
  },
  mounted() {
    this.loadCustomers();
  },
  computed: {
    getProjectItems() {
      if (!this.selectedCustomer || !this.selectedDepartment) return [];
      const department = this.selectedCustomer.departments.find(d => d.id === this.selectedDepartment);
      return department ? department.projects : [];
    }
  },
  methods: {
    async loadCustomers() {
      try {
        const user = this.$store.getters.userProfile;
        let customers = [];
        if (user && user.role === 'company_admin' && user.companyId) {
          // Only fetch their own company
          const db = getDatabase();
          const companyRef = dbRef(db, `customers/${user.companyId}`);
          const snap = await get(companyRef);
          customers = snap.exists() ? [{ ...snap.val() }] : [];
        } else if (user && user.role === 'super_admin') {
          // Fetch all customers
          const { loadCustomers } = await import('@/workshop/workshopFunctions');
          customers = Object.values(await loadCustomers());
        } else {
          customers = [];
        }
        console.log('Filtered customers for display:', customers);
        this.customerList = customers;
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    },

    getDepartmentItems(customer) {
      return customer?.departments || [];
    },

    openWorkshop(customerId, workshopId) {
      this.$store.dispatch('saveCustomerId', customerId);
      this.$store.commit('setWorkshopId', workshopId);
      this.$router.push(`/customer/${customerId}/workshop/${workshopId}`);
    },

    // Customer management
    startEditingCustomer(customerId) {
      // Toggle customer editing; clicking again stops editing
      if (this.editingCustomer === customerId) {
        this.editingCustomer = null;
      } else {
        this.editingCustomer = customerId;
      }
    },

    async saveCustomerName(customer) {
      if (this.editingCustomer === customer.id) {
        try {
          await updateCustomerName(customer.id, customer.name);
          await this.loadCustomers();
        } catch (error) {
          console.error('Error updating customer name:', error);
        }
        this.editingCustomer = null;
      }
    },

    async createCustomer() {
      if (this.newCustomerName.trim()) {
        try {
          await createCustomer(this.newCustomerName);
          this.newCustomerDialog = false;
          this.newCustomerName = "";
          await this.loadCustomers();
        } catch (error) {
          console.error("Error creating customer:", error);
        }
      }
    },

    confirmDeleteCustomer(customerId, name) {
      this.customerToDelete = { id: customerId, name: name };
      this.deleteCustomerDialog = true;
    },

    async deleteCustomerConfirmed() {
      try {
        await deleteCustomer(this.customerToDelete.id);
        this.deleteCustomerDialog = false;
        await this.loadCustomers();
      } catch (error) {
        console.error("Error deleting customer:", error);
      }
    },

    // Department management
    openNewDepartmentDialog(customerId) {
      this.selectedCustomer = this.customerList.find(c => c.id === customerId);
      this.newDepartmentDialog = true;
    },

    async createDepartment() {
      if (this.newDepartmentName.trim() && this.selectedCustomer) {
        try {
          await createDepartment(this.selectedCustomer.id, this.newDepartmentName);
          this.newDepartmentDialog = false;
          this.newDepartmentName = "";
          await this.loadCustomers();
        } catch (error) {
          console.error("Error creating department:", error);
        }
      }
    },

    // Initiative management
    openWorkshopDialog(customer) {
      this.selectedCustomer = customer;
      this.selectedDepartment = null;
      this.selectedProject = null;
      this.newWorkshopName = "";
      this.newWorkshopDialog = true;
    },

    async createNewInitiative() {
      if (this.newWorkshopName.trim() && this.selectedCustomer) {
        try {
          await createWorkshop(
            this.selectedCustomer.id,
            this.newWorkshopName
          );
          this.closeInitiativeDialog();
          await this.loadCustomers();
        } catch (error) {
          console.error('Error creating initiative:', error);
        }
      }
    },

    closeInitiativeDialog() {
      this.newWorkshopDialog = false;
      this.newWorkshopName = "";
      this.selectedDepartment = null;
      this.selectedProject = null;
    },

    viewDashboard(customerId) {
      this.$router.push(`/customer/${customerId}/dashboard`);
    }
  }
};
</script>

<style scoped>
.customer-selection {
  height: 100%;
  overflow-y: auto;
}

.customers-container {
  margin-top: 1rem;
}

.custom-panel {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.panel-title {
  min-height: 48px;
}

.initiative-card {
  min-width: 200px;
  max-width: 300px;
  cursor: pointer;
}

.initiatives-scroll {
  overflow-x: auto;
  padding-bottom: 1rem;
}

.initiatives-row {
  display: flex;
  flex-wrap: nowrap;
}

.workshop-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Prevent collapsing edit field for customer name in panel title */
.customer-edit-field {
  flex: 1 1 360px;
  width: 100%;
  min-width: 240px;
  max-width: 560px;
}
</style>
